import React, { useEffect, useState } from "react";
import html2pdf from "html2pdf.js";

export default function SiteReports() {
  const [sites, setSites] = useState([]);
  const [completedSessions, setCompletedSessions] = useState({});

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    const res = await fetch(`https://db2sg.com/tagweigh/api/sites?_t=${Date.now()}`);
    const data = await res.json();
    setSites(data);
    data.forEach((site) => fetchCompletedSessions(site));
  };

  const fetchCompletedSessions = async (site) => {
    const res = await fetch(
      `https://db2sg.com/tagweigh/api/sales?action=getCompletedSessions&siteMac=${site.mac}`
    );
    const result = await res.json();
    if (result.success) {
      setCompletedSessions((prev) => ({ ...prev, [site.mac]: result.data }));
    }
  };

  // =====================================
  // CORE REPORT HANDLER
  // =====================================
  const handleReportAction = async (site, sessionId, actionType) => {
    const res = await fetch(
      `https://db2sg.com/tagweigh/api/sales?action=getSessionReport&siteMac=${site.mac}&session_id=${sessionId}`
    );
    const result = await res.json();
    if (!result.success) return alert("Report error");

    const rows = result.data.map((row, index) => `
        <tr style="text-align:center">
          <td style="padding:8px; border:1px solid #ddd;">${index + 1}</td>
          <td style="padding:8px; border:1px solid #ddd;">${row.id_type}</td>
          <td style="padding:8px; border:1px solid #ddd;">${row.id_value}</td>
          <td style="padding:8px; border:1px solid #ddd;">${row.weight}</td>
          <td style="padding:8px; border:1px solid #ddd;">sold</td>
          <td style="padding:8px; border:1px solid #ddd;">${row.date_time}</td>
        </tr>`).join("");

    const html = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2 style="text-align:center; color: #333;margin-bottom:10px">Sale Report</h2>
        <hr/>
        <p><strong>Site:</strong> ${result.site_name}</p>
        <p><strong>Total Records:</strong> ${result.total_records}</p>
        <p><strong>Date:</strong> ${new Date()?.toISOString()?.replaceAll("T"," ")?.replaceAll("Z","")}</p>

        <table style="width:100%; border-collapse:collapse; margin-top:20px;">
          <thead style="background-color: #e0e0f2;">
            <tr>
              <th style="padding:8px; border:1px solid #ddd; padding-top:2px;padding-top:2px">SR</th>
              <th style="padding:8px; border:1px solid #ddd;padding-top:2px;padding-top:2px">Type</th>
              <th style="padding:8px; border:1px solid #ddd;padding-top:2px;padding-top:2px">ID</th>
              <th style="padding:8px; border:1px solid #ddd;padding-top:2px;padding-top:2px">Weight</th>
              <th style="padding:8px; border:1px solid #ddd;padding-top:2px;padding-top:2px">Remarks</th>
              <th style="padding:8px; border:1px solid #ddd;padding-top:2px;padding-top:2px">Date</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;

    const fileName = `Sale_Report_${site.name}_${sessionId}.pdf`;
    const worker = html2pdf().from(html).set({
      margin: 10,
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    });

    if (actionType === 'download') {
      worker.save();
    } 
    
    else if (actionType === 'print') {
      // Generate PDF as blob and open in new tab for printing
      worker.output('blob').then((blob) => {
        const url = URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        printWindow.onload = () => {
          printWindow.print();
        };
      });
    } 
    
    else if (actionType === 'share') {
      // Use Web Share API (Mobile friendly)
      worker.output('blob').then(async (blob) => {
        const file = new File([blob], fileName, { type: 'application/pdf' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Sale Report',
              text: `Check out the sale report for ${site.name}`,
            });
          } catch (error) {
            console.log('Sharing failed', error);
          }
        } else {
          alert("Web Share is not supported on this browser/device.");
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100  ">
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sites.map((site) => (
          <div key={site.mac+completedSessions[site.mac]} className="bg-white rounded-2xl shadow-xl flex flex-col h-[450px]">
            {/* 1. Header: Fixed at the top */}
            <div className="p-6 border-b">
              <h2 className="font-bold text-xl text-blue-600 truncate" title={site.name}>
                {site.name}
              </h2>
              <p className="text-gray-500 text-xs mt-1 uppercase tracking-wider font-semibold">
                {site.mac}
              </p>
            </div>
  
            {/* 2. Scrollable Sessions Area: Takes remaining space */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
              <p className="font-semibold text-sm text-gray-700 mb-2 sticky top-0 bg-white py-1">
                Completed Sessions ({completedSessions[site.mac]?.length || 0})
              </p>
  
              {completedSessions[site.mac]?.map((session) => (
                <div 
                  key={session.id} 
                  className="bg-gray-50 p-3 rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-sm transition-all"
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-bold text-gray-700">Session #{session.id}</span>
                    <span className="text-[10px] text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
                      {session.total_records} Records
                    </span>
                  </div>
                  
                  <div className="flex gap-1.5">
                    <button 
                      onClick={() => handleReportAction(site, session.id, 'download')}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-[10px] py-1.5 rounded-lg font-medium transition"
                    >
                      Download
                    </button>
                    <button 
                      onClick={() => handleReportAction(site, session.id, 'print')}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] py-1.5 rounded-lg font-medium transition"
                    >
                      Print
                    </button>
                    <button 
                      onClick={() => handleReportAction(site, session.id, 'share')}
                      className="flex-1 bg-violet-600 hover:bg-violet-700 text-white text-[10px] py-1.5 rounded-lg font-medium transition"
                    >
                      Share
                    </button>
                  </div>
                </div>
              ))}
  
              {!completedSessions[site.mac]?.length && (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <p className="text-sm text-gray-400">No completed sessions yet.</p>
                </div>
              )}
            </div>
            
            {/* 3. Footer: Optional summary or padding */}
            {/* <div className="p-3 bg-gray-50 rounded-b-2xl border-t text-center">
               <span className="text-[10px] text-gray-400 italic">Total Weight: {completedSessions[site.mac]?.reduce((acc, s) => acc + (parseFloat(s.total_weight) || 0), 0).toFixed(2)} kg</span>
            </div> */}
          </div>
        ))}
      </div>
    </div>
  );
}